import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const STATIC_FIELD_LABELS = {
  contact_number: "Contact Number",
  contact_mode: "Contact Mode",
  language: "Language",
  customer_name: "Customer Name",
  main_category_id: "Main Category",
  sub_category_id: "Sub-Category",
  issue_type_id: "Issue Type",
  branch: "Branch / Location",
  customer_concern: "Customer Concern",
};
const FIELD_ORDER = [
  "contact_number", "contact_mode", "language", "customer_name",
  "main_category_id", "sub_category_id", "issue_type_id", "branch", "customer_concern",
];
const FIELD_SELECTOR = {
  contact_number: '[data-testid="contact-number-input"]',
  contact_mode: '[data-testid="contact-mode-select"]',
  language: '[data-testid="language-select"]',
  customer_name: '[data-testid="customer-name-input"]',
  main_category_id: '[data-testid="main-category-select"]',
  sub_category_id: '[data-testid="sub-category-select"]',
  issue_type_id: '[data-testid="issue-type-select"]',
  branch: '[data-testid="branch-select"]',
  customer_concern: '[data-testid="customer-concern-input"]',
};

/**
 * Owns validation *surfacing* for the New Ticket form: touched state, the
 * submit-attempt flag, the ordered missing-fields list (for the summary +
 * tooltip), per-field error gating (blur / submit), and the scroll-to-first
 * behaviour. The actual `errors` object is computed by useNewTicketForm.
 */
export function useNewTicketValidation({ errors, dynamicFields, open }) {
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const markTouched = (key) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  const reset = () => { setTouched({}); setSubmitAttempted(false); };

  // Clear validation surfacing whenever the dialog opens or closes.
  useEffect(() => { setTouched({}); setSubmitAttempted(false); }, [open]);

  // Ordered list of currently-missing required fields.
  const missingFields = useMemo(() => {
    const out = [];
    FIELD_ORDER.forEach((k) => { if (errors[k]) out.push({ key: k, label: STATIC_FIELD_LABELS[k] }); });
    (dynamicFields || []).forEach((d) => {
      if (errors[`cf_${d.key}`]) out.push({ key: `cf_${d.key}`, label: d.label });
    });
    return out;
  }, [errors, dynamicFields]);

  // Surface an error only once the field is touched OR a submit was attempted.
  const showError = (key) => ((submitAttempted || touched[key]) ? errors[key] : undefined);

  /** Returns true when the form is invalid (and has flagged + scrolled to it). */
  const gateInvalid = () => {
    if (Object.keys(errors).length === 0) return false;
    setSubmitAttempted(true);
    const first = missingFields[0];
    if (first) {
      const sel = FIELD_SELECTOR[first.key]
        || (first.key.startsWith("cf_") ? '[data-testid="dynamic-fields-section"]' : null);
      if (sel) {
        setTimeout(() => {
          const el = document.querySelector(sel);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            try { el.focus({ preventScroll: true }); } catch { /* selects can't focus */ }
          }
        }, 0);
      }
    }
    toast.error("Please complete all required fields.");
    return true;
  };

  return {
    submitAttempted,
    missingFields,
    validation: { err: showError, touch: markTouched },
    gateInvalid,
    reset,
  };
}
